import { Injectable } from '@angular/core';
import { API_BASE } from './api-config';
import { createResourceFactory } from './base.resource';
import type { ResourceFactory } from './resource.types';

export { API_BASE };

/**
 * Centralized API resource service providing factory-based access to all API domains.
 *
 * Each domain is a factory function that creates independent resource instances,
 * ensuring no state interference between different consumers.
 *
 * Types are intentionally generic (unknown). Consumers should import specific types
 * from domain libraries:
 * - `@zag/planning/ui` for Skill, Team, Strategy, Plan, Employee
 * - `@zag/planning/common` for arktype schemas (CreateSkill, UpdateSkill, etc.)
 * - `@zag/core/models` for Person, pagination types
 *
 * @example
 * ```typescript
 * import { type Skill } from '@zag/planning/ui';
 *
 * @Component({ ... })
 * export class SkillsComponent {
 *   private readonly api = inject(ApiResourceService);
 *
 *   // Create an independent list resource with type
 *   readonly skills = this.api.skills() as ListResource<Skill>;
 *
 *   // Or use type assertion at the factory level
 *   readonly typedSkills = (this.api.skills as ResourceFactory<Skill>)();
 *
 *   onDelete(skill: Skill) {
 *     this.api.skills.delete(skill.id);
 *   }
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ApiResourceService {
  // ============================================
  // Planning Domain
  // ============================================

  /**
   * Skills catalog resource factory
   * @path /api/v1/core/skills
   */
  readonly skills: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/core/skills`,
  });

  /**
   * Teams resource factory
   * @path /api/v1/planning/teams
   */
  readonly teams: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/planning/teams`,
  });

  /**
   * Strategies resource factory
   * @path /api/v1/planning/strategies
   */
  readonly strategies: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/planning/strategies`,
  });

  /**
   * Plans resource factory
   * @path /api/v1/planning/plans
   */
  readonly plans: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/planning/plans`,
  });

  /**
   * Employees resource factory
   * @path /api/v1/planning/employees
   */
  readonly employees: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/planning/employees`,
  });

  /**
   * Shifts resource factory
   * @path /api/v1/planning/shifts
   */
  readonly shifts: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/planning/shifts`,
  });

  /**
   * Shift Definitions resource factory
   * @path /api/v1/planning/shift-definitions
   */
  readonly shiftDefinitions: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/planning/shift-definitions`,
  });

  // ============================================
  // Core Domain
  // ============================================

  /**
   * Persons resource factory
   * @path /api/v1/core/persons
   */
  readonly persons: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/core/persons`,
  });

  /**
   * Locations resource factory
   * @path /api/v1/core/locations
   */
  readonly locations: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/core/locations`,
  });

  // ============================================
  // Calendar Domain
  // ============================================

  /**
   * Calendars resource factory
   * @path /api/v1/calendar/calendars
   */
  readonly calendars: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/calendar/calendars`,
  });

  /**
   * Slot type definitions resource factory
   * @path /api/v1/calendar/slot-type-definitions
   */
  readonly slotTypeDefinitions: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/calendar/slot-type-definitions`,
  });

  // ============================================
  // Time Tracking Domain
  // ============================================

  /**
   * Balance type definitions resource factory
   * @path /api/v1/time-tracking/balance-type-definitions
   */
  readonly balanceTypeDefinitions: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/time-tracking/balance-type-definitions`,
  });

  // ============================================
  // RBAC Domain
  // ============================================

  /**
   * Users resource factory (read-only create - users are created via auth)
   * @path /api/v1/rbac/users
   */
  readonly users: ResourceFactory<unknown, never, unknown> = createResourceFactory<unknown, never, unknown>({
    basePath: `${API_BASE}/rbac/users`,
  });

  /**
   * Groups resource factory
   * @path /api/v1/rbac/groups
   */
  readonly groups: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/rbac/groups`,
  });

  /**
   * Roles resource factory
   * @path /api/v1/rbac/roles
   */
  readonly roles: ResourceFactory<unknown, unknown, unknown> = createResourceFactory<unknown, unknown, unknown>({
    basePath: `${API_BASE}/rbac/roles`,
  });

  /**
   * Create a typed resource factory for the given path, with `itemSuffix`
   * appended to single-item GET requests only (e.g. `?include=translations`).
   * List endpoints remain unaffected.
   *
   * @example
   * ```ts
   * const factory = this.api.createWithItemSuffix<LocationResponse>(
   *   'core/locations',
   *   '?include=translations',
   * );
   * ```
   */
  createWithItemSuffix<T>(path: string, itemSuffix: string): ResourceFactory<T, unknown, unknown> {
    return createResourceFactory<T, unknown, unknown>({
      basePath: `${API_BASE}/${path}`,
      itemSuffix,
    });
  }
}
